/**
 * Ticket 96: "Create a volume input slider for every user on rooms ... Allow
 * users to increase their input volume up to 3x."
 *
 * INPUT, not output: this makes YOUR OWN microphone louder for everybody else
 * in the room. The per-participant sliders under each tile are a different
 * control for a different problem (how loud other people are to me) and are
 * deliberately untouched by this file.
 *
 * An outgoing MediaStreamTrack has no volume property of any kind — a
 * microphone is not a speaker — so amplification has to be real signal
 * processing: the captured track is routed through a Web Audio GainNode and the
 * graph's output is what gets published.
 *
 * WHY A PROCESSOR RATHER THAN replaceTrack
 * ----------------------------------------
 * The screen-share audio mix in `useRoomAudio` splices its gain in with
 * `LocalAudioTrack.replaceTrack()`, and doing the same to the microphone looks
 * like the consistent choice. It is not: `replaceTrack` sets LiveKit's
 * `providedByUser` flag, and that flag is what gates LiveKit's OWN microphone
 * recovery — reacquiring the device when it changes or ends, and restarting
 * capture after a phone has backgrounded the page. This room code depends on
 * both (see `shouldRestoreMicrophone`). Taking ownership of the mic to make it
 * louder would quietly cost us the machinery that keeps it working at all.
 *
 * `setProcessor` keeps LiveKit in charge, and goes further: when it re-acquires
 * the microphone it calls `processor.restart()` with the new device track and
 * re-publishes the processed output itself, so a boost survives a device change
 * without us watching for one.
 *
 * It does NOT survive the track being unpublished and a fresh one created —
 * which is exactly what a demote-to-listener-then-promote does. That is why
 * `applyMicGain` is idempotent and re-asserted whenever a mic track is
 * published, rather than being fired once and trusted.
 */

import type {
  AudioProcessorOptions,
  Track,
  TrackProcessor,
} from 'livekit-client';

/** 3x, the ceiling the ticket asked for. Deliberately not enforced anywhere else. */
export const MIC_GAIN_MAX_PERCENT = 300;

/**
 * The floor is 50%, not 0. A slider that can silence you while the microphone
 * button says you are unmuted is a "why can't anyone hear me" trap with no
 * visible cause; going silent is the mute button's job and it already says so.
 */
export const MIC_GAIN_MIN_PERCENT = 50;

/** No boost, no processing, nobody's audio path altered. */
export const MIC_GAIN_DEFAULT_PERCENT = 100;

export const clampMicGainPercent = (value: number): number => {
  if (!Number.isFinite(value)) return MIC_GAIN_DEFAULT_PERCENT;
  return Math.max(
    MIC_GAIN_MIN_PERCENT,
    Math.min(MIC_GAIN_MAX_PERCENT, Math.round(value)),
  );
};

/**
 * Who is offered the control. An ALLOWLIST of the roles that actually publish
 * audio, deliberately not `role !== 'listener'`: it is the same set as
 * `canPublish` in useRoomAudio, and if a new non-publishing role is ever added
 * the control is withheld by default rather than silently offered to someone
 * whose microphone does not exist.
 *
 * A listener publishes nothing, so there is no input for a gain to act on —
 * ticket 95's lesson, that a control which cannot work should not be drawn.
 */
export const canControlMicGain = (role: string | undefined): boolean =>
  role === 'host' || role === 'cohost' || role === 'speaker';

type AudioTrackProcessor = TrackProcessor<
  Track.Kind.Audio,
  AudioProcessorOptions
>;

/**
 * The narrow shape of `LocalAudioTrack` this module needs. Structural rather
 * than the class itself so the tests can drive the real orchestration with a
 * fake track — every existing test in this feature mocks livekit-client, and a
 * gain path that can only be tested against a live browser is a gain path
 * nobody tests.
 */
export interface MicGainTrack {
  mediaStreamTrack: MediaStreamTrack;
  setProcessor: (processor: AudioTrackProcessor) => Promise<void>;
  getProcessor?: () => unknown;
  setAudioContext?: (ctx: AudioContext | undefined) => void;
}

/**
 * Source -> gain -> destination, in a context we own. `dest.stream`'s track is
 * what LiveKit publishes in place of the raw microphone.
 */
interface GainGraph {
  ctx: AudioContext;
  source: MediaStreamAudioSourceNode;
  gain: GainNode;
  dest: MediaStreamAudioDestinationNode;
}

export class MicGainProcessor implements AudioTrackProcessor {
  public readonly name = 'nofilter-mic-gain';
  public processedTrack?: MediaStreamTrack;

  private graph: GainGraph | null = null;
  private level: number;

  public constructor(level: number) {
    this.level = level;
  }

  public init(opts: AudioProcessorOptions): Promise<void> {
    this.build(opts);
    return Promise.resolve();
  }

  /**
   * LiveKit calls this with the newly acquired device track after a device
   * change or a reacquiring unmute, then publishes whatever `processedTrack`
   * has become.
   *
   * The old graph is disconnected first. Not because it would be audible — the
   * previous device track is already stopped by the time we get here — but
   * because without it every device change leaves an orphaned source and gain
   * node wired together inside the context, and the context outlives them.
   */
  public restart(opts: AudioProcessorOptions): Promise<void> {
    this.teardown();
    this.build(opts);
    return Promise.resolve();
  }

  public destroy(): Promise<void> {
    this.teardown();
    this.processedTrack = undefined;
    return Promise.resolve();
  }

  /** Live, with no re-publish: dragging the slider only writes to gain.value. */
  public setLevel(level: number): void {
    this.level = level;
    if (this.graph) this.graph.gain.gain.value = level;
  }

  public getLevel(): number {
    return this.level;
  }

  private build(opts: AudioProcessorOptions): void {
    const ctx = opts.audioContext;
    const source = ctx.createMediaStreamSource(new MediaStream([opts.track]));
    const gain = ctx.createGain();
    gain.gain.value = this.level;
    const dest = ctx.createMediaStreamDestination();
    source.connect(gain);
    gain.connect(dest);
    this.graph = { ctx, source, gain, dest };
    this.processedTrack = dest.stream.getAudioTracks()[0];
  }

  private teardown(): void {
    const graph = this.graph;
    if (!graph) return;
    this.graph = null;
    try {
      graph.source.disconnect();
      graph.gain.disconnect();
    } catch {
      /* already torn down by the context closing */
    }
  }
}

export interface MicGainInstallation {
  processor: MicGainProcessor;
  /** Ours to close. LiveKit's own context must never be closed from here. */
  ctx: AudioContext;
}

export interface ApplyMicGainOptions {
  /** 50..300. 100 means "do not touch the audio path at all". */
  percent: number;
  track: MicGainTrack | null | undefined;
  /** The installation from the previous call, or null on the first. */
  current: MicGainInstallation | null;
  createContext: () => AudioContext | null;
  log: (msg: string) => void;
}

/**
 * Bring the published microphone into line with `percent`, and return the
 * installation to remember. Idempotent, and safe to call on every mic
 * publication — which is the point, since that is the only way a boost survives
 * being demoted to listener and promoted again.
 *
 * Deliberately does nothing at all at 100%: a user who never boosts never has
 * an AudioContext built for them and their microphone is published exactly as
 * it always was. Once installed the processor STAYS installed, including on the
 * way back down to 100% — it simply sits at unity gain. Repeatedly installing
 * and removing it would swap the published track on every crossing of 100%,
 * and a sender swap is not free.
 */
export const applyMicGain = async ({
  percent,
  track,
  current,
  createContext,
  log,
}: ApplyMicGainOptions): Promise<MicGainInstallation | null> => {
  if (!track) return current;

  const level = clampMicGainPercent(percent) / 100;

  // Still installed on THIS track: nothing to publish, just move the knob.
  if (current && track.getProcessor?.() === current.processor) {
    current.processor.setLevel(level);
    return current;
  }

  // Either we have never installed, or the track we installed on has been
  // unpublished (role change) — LiveKit destroys that processor with the track,
  // so there is nothing of ours left to clean up but the context.
  if (current) {
    void current.ctx.close().catch(() => undefined);
  }

  if (percent === MIC_GAIN_DEFAULT_PERCENT) return null;

  const ctx = createContext();
  if (!ctx) {
    // No Web Audio at all. Say so rather than leaving a slider showing 300%
    // while the microphone publishes at 1x.
    log('[Room] MIC_GAIN unavailable: no Web Audio, input stays at 100%');
    return null;
  }

  try {
    await ctx.resume().catch(() => undefined);
    // LiveKit refuses setProcessor outright unless a context is set on the
    // track, and whether it has one depends on connect-time ordering we do not
    // control. Ours is the one we can reason about, and on a LOCAL audio track
    // the context is used for nothing but processor options.
    track.setAudioContext?.(ctx);
    const processor = new MicGainProcessor(level);
    await track.setProcessor(processor);
    log(`[Room] MIC_GAIN engaged at ${clampMicGainPercent(percent)}%`);
    return { processor, ctx };
  } catch (err) {
    void ctx.close().catch(() => undefined);
    log(`[Room] MIC_GAIN failed: ${String(err)}`);
    return null;
  }
};

/** Room teardown. Closes only the context we created. */
export const releaseMicGain = (current: MicGainInstallation | null): void => {
  if (!current) return;
  void current.ctx.close().catch(() => undefined);
};
